'use client';

import { useMemo, useState } from 'react';

import { Badge } from '@core/ui/primitives/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@core/ui/primitives/collapsible';
import { Icon } from '@core/ui/primitives/icon';
import { Textarea } from '@core/ui/primitives/textarea';
import { cn } from '@core/helpers/misc/GeneralUtils';

export interface FeatureOption {
  id: string;
  name: string;
  description: string;
  category: string;
  defaultEnabled: boolean;
}

interface FeaturePickerProps {
  /** Currently selected feature/entitlement IDs */
  value: string[];
  /** Callback when selection changes */
  onChange: (value: string[]) => void;
  /** Available features to choose from */
  features: FeatureOption[];
  /** Input name for form submission (hidden input with JSON value) */
  name?: string;
  /** Whether the picker is disabled */
  disabled?: boolean;
}

const CATEGORY_LABELS: Record<string, string> = {
  search: 'Search',
  collaboration: 'Collaboration',
  content: 'Content',
  ai: 'AI Features',
  admin: 'Administration',
};

const CATEGORY_ICONS = {
  search: 'LuSearch',
  collaboration: 'LuMessageSquare',
  content: 'LuFileText',
  ai: 'LuSparkles',
  admin: 'LuSettings',
} as const;

export function FeaturePicker({ value, onChange, features, name, disabled }: FeaturePickerProps) {
  const [showRaw, setShowRaw] = useState(false);

  const featuresByCategory = useMemo(() => {
    const result = new Map<string, FeatureOption[]>();
    for (const feature of features) {
      const existing = result.get(feature.category) ?? [];
      existing.push(feature);
      result.set(feature.category, existing);
    }
    return result;
  }, [features]);

  const selectedSet = useMemo(() => new Set(value), [value]);

  // Find any raw values that aren't known features (escape hatch values)
  const knownIds = useMemo(() => new Set(features.map((f) => f.id)), [features]);
  const rawValues = useMemo(() => value.filter((v) => !knownIds.has(v)), [value, knownIds]);

  const handleToggle = (featureId: string, checked: boolean) => {
    if (checked) {
      onChange([...value, featureId]);
    } else {
      onChange(value.filter((v) => v !== featureId));
    }
  };

  const handleRawChange = (rawText: string) => {
    const rawIds = rawText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    // Keep known feature selections, replace raw values
    const knownSelections = value.filter((v) => knownIds.has(v));
    onChange([...knownSelections, ...rawIds]);
  };

  return (
    <div className='space-y-4'>
      {/* Hidden input for form submission */}
      {name && <input type='hidden' name={name} value={JSON.stringify(value)} />}

      {/* Feature categories */}
      {Array.from(featuresByCategory.entries()).map(([category, categoryFeatures]) => (
        <div key={category} className='space-y-2'>
          <div className='flex items-center gap-2 text-sm font-medium text-muted-foreground'>
            <Icon name={CATEGORY_ICONS[category as keyof typeof CATEGORY_ICONS] ?? 'LuBox'} className='h-4 w-4' />
            {CATEGORY_LABELS[category] ?? category}
          </div>
          <div className='space-y-1 pl-6'>
            {categoryFeatures.map((feature) => (
              <label
                key={feature.id}
                className={cn(
                  'flex items-start gap-3 rounded-md p-2 transition-colors cursor-pointer',
                  'hover:bg-muted/50',
                  disabled && 'opacity-50 cursor-not-allowed'
                )}>
                <input
                  type='checkbox'
                  checked={selectedSet.has(feature.id)}
                  onChange={(e) => handleToggle(feature.id, e.target.checked)}
                  disabled={disabled}
                  className='mt-1 h-4 w-4 accent-primary'
                />
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2'>
                    <span className='text-sm font-medium'>{feature.name}</span>
                    {feature.defaultEnabled && (
                      <Badge variant='secondary' className='text-[10px]'>
                        Default
                      </Badge>
                    )}
                  </div>
                  <p className='text-xs text-muted-foreground line-clamp-2'>{feature.description}</p>
                </div>
              </label>
            ))}
          </div>
        </div>
      ))}

      {/* Escape hatch for raw privilege IDs */}
      <Collapsible open={showRaw} onOpenChange={setShowRaw}>
        <CollapsibleTrigger
          className={cn(
            'flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors',
            disabled && 'pointer-events-none opacity-50'
          )}>
          <Icon name={showRaw ? 'LuChevronDown' : 'LuChevronRight'} className='h-3 w-3' />
          Advanced: Custom entitlements
        </CollapsibleTrigger>
        <CollapsibleContent className='pt-2'>
          <div className='space-y-2 pl-5'>
            <Textarea
              value={rawValues.join('\n')}
              onChange={(e) => handleRawChange(e.target.value)}
              placeholder='custom:feature-id&#10;legacy:old-feature'
              rows={3}
              disabled={disabled}
              className='font-mono text-xs bg-muted/40'
            />
            <p className='text-xs text-muted-foreground'>
              One ID per line. Use for custom or legacy entitlements not in the picker above.
            </p>
          </div>
        </CollapsibleContent>
      </Collapsible>

      {/* Summary */}
      {value.length > 0 && (
        <div className='flex flex-wrap gap-1 pt-2 border-t'>
          {value.map((id) => (
            <Badge key={id} variant='outline' className='text-[10px] font-mono'>
              {id}
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
